/* ============================================================
   HANJA readiness — le due soglie del contratto proposto al
   sistema esterno partner Frabemar (Fase A/B/D, 2026-07-23).

   Sostituisce, come indicatore PRIMARIO mostrato all'operatore, la
   vecchia metrica interna a 26 campi core (che resta calcolata lato
   BE per compatibilita' ma non e' piu' quella su cui si agisce).

   - "minimo": bl_number presente — determina se la pratica avrebbe
     un ID da HANJA (a livello dati; user/file BL si verificano a parte).
   - "completo": 28 campi (27 scalari + regola "almeno un tipo di
     trasporto completo") — determina se il certificato sarebbe
     "working complete" pronto per convalida.

   Nessuna soglia qui blocca l'invio: oggi non esiste alcun invio
   automatico a HANJA (nessun endpoint/account, sempre operatore in
   mezzo — vedi docs/integrazione-hanja/README.md). Sono solo
   indicatori informativi per l'operatore.
   ============================================================ */

import type { BscHanjaReadiness } from "../api/types";

/** Id del certificato restituito da HANJA — in evidenza, con stato esplicito.
 *
 *  Resta vuoto finche' non inviamo davvero (endpoint e credenziali HANJA non
 *  ancora forniti): in quel caso mostra "Non ancora inviato" invece di un
 *  trattino muto, cosi' l'operatore sa che e' atteso e non un dato perso.
 *  Quando valorizzato e' il riferimento con cui parlare della pratica col
 *  cliente, quindi va reso selezionabile/copiabile: `user-select: all`.
 *
 *  ``compact``: variante inline per l'INTESTAZIONE della pagina, accanto a
 *  titolo e stato. Richiesta esplicita di Massimo Bernardini in call il
 *  2026-07-29 — "io le metterei addirittura in testa, perche' poi quello
 *  diventa il vero riferimento univoco" (uno per pratica).
 */
export function HanjaCertificateId({
  value, compact = false,
}: { value?: string | null; compact?: boolean }) {
  const presente = Boolean(value);
  if (compact) {
    return (
      <span
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "2px 8px",
          borderRadius: "var(--border-radius-sm)",
          border: `1px solid ${presente ? "var(--indaco-success)" : "var(--indaco-gray-300)"}`,
          background: presente ? "var(--indaco-success-light)" : "var(--indaco-gray-50)",
          fontSize: "var(--font-size-xs)", whiteSpace: "nowrap",
        }}
        title={presente
          ? "Id del certificato su HANJA — riferimento univoco della pratica"
          : "Verra' valorizzato al primo invio a HANJA (endpoint non ancora disponibile)"}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
          {presente ? "verified" : "pending"}
        </span>
        <span className="text-muted">Cert. ID</span>
        <strong
          className={presente ? undefined : "text-muted"}
          style={{
            fontFamily: presente ? "var(--font-family-mono)" : undefined,
            userSelect: presente ? "all" : undefined,
          }}
        >
          {presente ? value : "non inviato"}
        </strong>
      </span>
    );
  }
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: "var(--space-2)",
        padding: "var(--space-2) var(--space-3)",
        marginBottom: "var(--space-3)",
        borderRadius: "var(--border-radius-md)",
        border: `1px solid ${presente ? "var(--indaco-success)" : "var(--indaco-gray-300)"}`,
        background: presente ? "var(--indaco-success-light)" : "var(--indaco-gray-50)",
      }}
      title={presente
        ? "Id del certificato su HANJA"
        : "Verra' valorizzato al primo invio a HANJA (endpoint non ancora disponibile)"}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
        {presente ? "verified" : "pending"}
      </span>
      <span className="text-xs text-muted">Certificate ID</span>
      <strong
        className={presente ? undefined : "text-muted"}
        style={{
          marginLeft: "auto",
          fontFamily: presente ? "var(--font-family-mono)" : undefined,
          fontSize: presente ? "var(--font-size-base)" : "var(--font-size-xs)",
          userSelect: presente ? "all" : undefined,
        }}
      >
        {presente ? value : "Non ancora inviato"}
      </strong>
    </div>
  );
}

// Etichette italiane per i campi del set "obbligatorio-per-completo" —
// usate SOLO per la lista informativa di cosa manca (non sono chip
// cliccabili come nel pannello 26-core: molti di questi campi non hanno
// ancora un input dedicato in form, vedi nota in coda al file).
const HANJA_FIELD_LABELS: Record<string, string> = {
  date_bl: "Data BL",
  carrier: "Carrier",
  eta: "ETA (arrivo)",
  ets: "ETS (partenza)",
  exporter_address: "Indirizzo esportatore",
  exporter_country: "Paese esportatore",
  exporter_mail: "Email esportatore",
  exporter_name: "Ragione sociale esportatore",
  final_destination: "Destinazione finale",
  fob: "Valore FOB",
  fob_currency: "Valuta FOB",
  forwarder_name: "Forwarder",
  freight: "Costo nolo",
  freight_currency: "Valuta nolo",
  freight_payment_terms: "Modalità pagamento nolo",
  goods_origin_country: "Paese origine merce",
  importer_address: "Indirizzo importatore",
  importer_country: "Paese importatore",
  importer_mail: "Email importatore",
  importer_name: "Ragione sociale importatore",
  invoice_date: "Data fattura",
  invoice_number: "Numero fattura",
  pod: "Porto di scarico",
  pol: "Porto di carico",
  terms_of_delivery: "Incoterm",
  vessel_name: "Nave",
  voyage_number: "Numero viaggio",
  transport_type_complete: "Almeno un tipo di trasporto completo",
};

export function hanjaFieldLabel(field: string): string {
  return HANJA_FIELD_LABELS[field] ?? field;
}

function completeColor(nonNull: number, total: number): string {
  if (total === 0) return "var(--indaco-gray-400)";
  const pct = nonNull / total;
  if (pct >= 0.9) return "var(--indaco-success)";
  if (pct >= 0.5) return "var(--indaco-warning)";
  return "var(--indaco-danger)";
}

/** Coppia di badge compatti per riga tabella/coda (BscList/BscQueue). */
export function HanjaBadges({ hanja }: { hanja?: BscHanjaReadiness | null }) {
  if (!hanja) {
    return <span className="badge badge-neutral badge-sm" title="Nessun dato HANJA">HANJA —</span>;
  }
  const { minimal_ready, complete_non_null, complete_total } = hanja;
  return (
    <span style={{ display: "inline-flex", gap: "var(--space-1)", alignItems: "center" }}>
      <span
        className={`badge badge-sm ${minimal_ready ? "badge-success" : "badge-danger"}`}
        title={minimal_ready ? "Campi minimi HANJA presenti (bl_number)" : "Manca il numero BL — nessun ID HANJA ottenibile"}
      >
        Min {minimal_ready ? "✓" : "✗"}
      </span>
      <span
        className="badge badge-sm"
        style={{ background: completeColor(complete_non_null, complete_total), color: "white" }}
        title={`Completo HANJA: ${complete_non_null}/${complete_total} campi (incl. almeno un tipo di trasporto)`}
      >
        {complete_non_null}/{complete_total}
      </span>
    </span>
  );
}

/** Pannello completo per il dettaglio pratica: doppia barra + campi mancanti + warning FOB. */
export function HanjaReadinessPanel({ hanja }: { hanja?: BscHanjaReadiness | null }) {
  if (!hanja) {
    return (
      <div className="text-sm text-muted">Dati HANJA non ancora calcolati per questa pratica.</div>
    );
  }
  const { minimal_ready, complete_non_null, complete_total, complete_missing, fob_consistency } = hanja;
  const completePct = complete_total > 0 ? complete_non_null / complete_total : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--space-1)" }}>
          <span className="text-sm">Minimo per ID HANJA</span>
          <span className={`badge badge-sm ${minimal_ready ? "badge-success" : "badge-danger"}`}>
            {minimal_ready ? "Presente" : "Manca il numero BL"}
          </span>
        </div>
      </div>

      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--space-1)" }}>
          <span className="text-sm">Obbligatorio per certificato completo</span>
          <span className="text-sm" style={{ color: completeColor(complete_non_null, complete_total), fontWeight: 600 }}>
            {complete_non_null}/{complete_total}
          </span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${Math.round(completePct * 100)}%`, background: completeColor(complete_non_null, complete_total) }}
          />
        </div>
      </div>

      {fob_consistency?.mismatch && (
        <div className="alert alert-warning" style={{ fontSize: "var(--font-size-sm)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: "middle" }}>warning</span>
          {" "}FOB estratto ({fob_consistency.extracted}) non coincide col derivato da totale-nolo-assicurazione
          ({fob_consistency.derived}, Δ {fob_consistency.delta}) — verificare.
        </div>
      )}

      {complete_missing.length > 0 && (
        <div>
          <div className="text-sm text-muted" style={{ marginBottom: "var(--space-1)" }}>
            Campi mancanti per il completo ({complete_missing.length}):
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-1)" }}>
            {complete_missing.map((f) => (
              <span key={f} className="badge badge-neutral badge-sm">{hanjaFieldLabel(f)}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
